import { FormLabel, InputField, SelectField } from '@/components/Form';
import {
  useFetchUserDataQuery,
  usePostUserDataMutation,
  useVerifyOTPMutation,
  useVerifyPhoneMutation,
} from '@/store/apis/userApi';
import { Button, Col, Flex, Modal, Row, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { UserProfile } from '@/types';
import {
  displayError,
  displayErrorText,
  displaySuccess,
} from '@/utils/displayMessageUtils';
import { yupResolver } from '@hookform/resolvers/yup';
import { profileSchema } from '../../schema/profileSchema';
import { getCountryData, ICountryData, TCountryCode } from 'countries-list';
import { useFetchCountryByIPQuery } from '@/store/apis/coreApi';
import { CheckOutlined } from '@ant-design/icons';

import { OTPModal } from '@/components/Elements';
import PhoneInputField from '@/components/Form/PhoneInputField';
import {
  useGetTFNNumberMutation,
  useSendOTPMutation,
  useVerifyUpdateTFNNumberMutation,
  useSendTFNOTPMutation,
  useVerifyTFNOTPMutation,
  useSendTFNUpdateOTPMutation,
  useVerifyTFNUpdateOTPMutation,
} from '../../api/profileAPI';
import TFNNumberEdit from './TFNNumberEdit';
import { Car } from 'lucide-react';
import { Grid } from 'antd';

const { useBreakpoint } = Grid;

const { useToken } = theme;

const Profile = () => {
  const { token } = useToken();
  const { data: profileData, refetch: refetchProfile } = useFetchUserDataQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [showTfnNumber, setShowTfnNumber] = useState(false);
  const [countryDetail, setCountryDetail] = useState<ICountryData | null>(null);
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [openTFNOTPModal, setOpenTFNOTPModal] = useState(false);
  const [openTFNUpateOTPModal, setOpenTFNUpdateOTPModal] = useState(false);
  const [showTFNEditModal, setShowTFNEditModal] = useState(false);

  const [verifyPhone, { isLoading: verifyPhoneLoading }] =
    useVerifyPhoneMutation();
  const [verifyOTP] = useVerifyOTPMutation();
  const [sendOTP, { isLoading: sendOTPLoading }] = useSendOTPMutation();
  const [getTFNNumber] = useGetTFNNumberMutation();

  const [verifyUpdateTFN] = useVerifyUpdateTFNNumberMutation();

  const [sendTFNOTP, { isLoading: sendTFNOTPLoading }] =
    useSendTFNOTPMutation();
  const [verifyTFNOTPAndView] = useVerifyTFNOTPMutation();

  const [sendTFNUpdateOTP, { isLoading: sendTFNUpdateOTPLoading }] =
    useSendTFNUpdateOTPMutation();
  const [verifyTFNUpdateOTP] = useVerifyTFNUpdateOTPMutation();

  const { data: countryData } = useFetchCountryByIPQuery();

  const profileDetail = profileData?.data;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(profileSchema),
  });

  const watchResidence = watch('countryCode');
  useEffect(() => {
    if (countryData) {
      setValue('countryCode', countryData.data.country);
    }
  }, [countryData]);

  useEffect(() => {
    if (watchResidence) {
      const countryDetail = getCountryData(watchResidence as TCountryCode);
      setCountryDetail(countryDetail);
    }
  }, [watchResidence]);

  const [postUserData, { isLoading: postUserDataLoading }] =
    usePostUserDataMutation();

  const countryOptions = useCountryOptions();

  useEffect(() => {
    console.log('profileDetail', profileDetail);
    if (profileDetail) {
      setValue('firstName', profileDetail?.firstName ?? '');
      setValue('lastName', profileDetail?.lastName ?? '');
      setValue('countryCode', profileDetail?.country ?? '');
      setValue('email', profileDetail?.email ?? '');
      if (profileDetail?.hasTfnNumber) {
        setValue('hashTFNNumber', profileDetail?.hashedTfnNumber);
      }

      // setValue(
      //   'phone',
      //   profileDetail.phone.slice(profileDetail?.country?.length) ?? '',
      // );

      // if (profileDetail.country) {
      //   const phoneWithoutCountryCode = extractPhoneNumber(
      //     profileDetail?.phone,
      //     profileDetail?.country,
      //   );
      //   setValue('phone', phoneWithoutCountryCode);
      // }
      setValue('phone', profileDetail?.phone ?? '');
    }
  }, [profileDetail]);

  const onSubmit = async (data: UserProfile) => {
    try {
      const filteredData = {
        phone: data?.phone ?? '',
        country: data?.countryCode ?? '',
        tfn_number: data?.tfnNumber ?? '',
        first_name: data?.firstName ?? '',
        last_name: data?.lastName ?? '',
      };
      await postUserData(filteredData).unwrap();
      displaySuccess('Successfully Updated Profile.');
      refetchProfile();
    } catch (err) {
      displayError('Something went wrong. Please try again later.');
    }
  };

  const handleVerifyPhone = () => {
    verifyPhone()
      .unwrap()
      .then((res) => {
        displaySuccess(res.data.message);
        setOpenOTPModal(true);
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const handleCancel = () => {
    setOpenOTPModal(false);
  };

  const handleVerify = async (otp: string) => {
    try {
      const response = await verifyOTP({ otp: otp }).unwrap();
      if (response.data.verification_status === 'pending') {
        displayErrorText('Invalid OTP. Please try again.');
      }
      if (response.data.verification_status === 'approved') {
        displaySuccess('Your phone number have been verified.');
        setOpenOTPModal(false);
        refetchProfile();
      }
    } catch (err) {
      displayErrorText('Invalid OTP. Please try again.');
    }
  };

  const handleViewTFNNumber = () => {
    sendTFNOTP()
      .unwrap()
      .then((res) => {
        console.log(res);
        displaySuccess(res.message);
        setOpenTFNOTPModal(true);
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const handleTFNViewCancel = () => {
    setOpenTFNOTPModal(false);
  };

  const handleTFNOTPVerify = async (otp: string) => {
    try {
      const res = await verifyTFNOTPAndView({ code: otp }).unwrap();
      displaySuccess(res?.message);
      setOpenTFNOTPModal(false);
      setValue('hashTFNNumber', res.data.tfn_number);
      setShowTfnNumber(true);
    } catch (err) {
      displayError(err);
    }
  };
  const handleEditTFNNumber = () => {
    setShowTFNEditModal(true);
  };

  const handleTFNSumbit = () => {
    setShowTFNEditModal(false);
    setOpenTFNUpdateOTPModal(true);
  };

  const handleTFNUpdatCancel = () => {
    setOpenTFNUpdateOTPModal(false);
  };

  const handleTFNOTPUpdateVerify = async (otp: string) => {
    try {
      const res = await verifyTFNUpdateOTP({ code: otp }).unwrap();
      displaySuccess(res?.message);
      setOpenTFNUpdateOTPModal(false);
      setValue('hashTFNNumber', res?.data?.tfn_number);
      setShowTfnNumber(true);
    } catch (err) {
      displayError(err);
    }
  };

  const { lg } = useBreakpoint();

  return (
    <>
      <div
        style={{
          width: '100%',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <>
            <Typography.Title
              level={3}
              style={{
                margin: '15px 0px',
              }}
            >
              Profile Details
            </Typography.Title>

            <Row gutter={30}>
              <Col xs={24} md={12}>
                <InputField
                  name="firstName"
                  control={control}
                  label="First Name"
                  size="large"
                  required
                  error={errors?.firstName?.message ?? ''}
                />
              </Col>
              <Col xs={24} md={12}>
                <InputField
                  name="lastName"
                  control={control}
                  label="Last Name"
                  size="large"
                  required
                  error={errors?.lastName?.message ?? ''}
                />
              </Col>
              <Col xs={24} md={12}>
                <InputField
                  disabled
                  name="email"
                  control={control}
                  label="Email"
                  size="large"
                  required
                  error={errors?.email?.message ?? ''}
                />
              </Col>
              <Col xs={24} md={12}>
                <SelectField
                  showSearch={true}
                  options={countryOptions}
                  name="countryCode"
                  control={control}
                  label="Country"
                  size="large"
                  placeholder="Select your country"
                  error={errors.countryCode?.message ?? ''}
                  // onBlur={handleBlur}
                  required={true}
                />
              </Col>
              <Col xs={24} md={12}>
                {profileDetail?.hasTfnNumber ? (
                  <InputField
                    label="TFN Number"
                    name="hashTFNNumber"
                    control={control}
                    placeholder="Enter your TFN Number"
                    size="large"
                    disabled={true}
                    suffix={
                      !showTfnNumber ? (
                        <Flex gap={15}>
                          <Button
                            size="small"
                            onClick={handleViewTFNNumber}
                            disabled={sendOTPLoading}
                            loading={sendOTPLoading}
                          >
                            View
                          </Button>
                          <Button size="small" onClick={handleEditTFNNumber}>
                            Edit
                          </Button>
                        </Flex>
                      ) : (
                        <Button size="small" onClick={handleEditTFNNumber}>
                          Edit
                        </Button>
                      )
                    }
                  />
                ) : (
                  <InputField
                    label="TFN Number"
                    name="tfnNumber"
                    control={control}
                    placeholder="Enter your TFN Number"
                    size="large"
                    type="password"
                  />
                )}
              </Col>
              <Col xs={24}>
                <div style={{ marginBottom: 24 }}>
                  <FormLabel label="Phone Number" />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: lg ? 'center' : 'flex-start',
                      gap: 12,
                      flexDirection: lg ? 'row' : 'column',
                    }}
                  >
                    <div style={{ flexGrow: 1, width: '100%' }}>
                      <PhoneInputField
                        name="phone"
                        control={control}
                        error={errors.phone?.message ?? ''}
                      />
                    </div>
                    {profileDetail?.isPhoneVerified ? (
                      <CheckOutlined
                        style={{
                          color: token.colorSuccess,
                          fontSize: 20,
                        }}
                      />
                    ) : (
                      <Button
                        size="large"
                        type="primary"
                        ghost
                        loading={verifyPhoneLoading}
                        onClick={handleVerifyPhone}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
          <Button
            loading={postUserDataLoading}
            type="primary"
            htmlType="submit"
            size="large"
          >
            Save
          </Button>
        </form>
      </div>
      <OTPModal
        visible={openOTPModal}
        onCancel={handleCancel}
        onVerify={handleVerify}
      />
      <OTPModal
        visible={openTFNOTPModal}
        onCancel={handleTFNViewCancel}
        onVerify={handleTFNOTPVerify}
        handleOtpResend={handleViewTFNNumber}
      />
      <Modal
        open={showTFNEditModal}
        onCancel={() => setShowTFNEditModal(false)}
        footer={null}
        title="Edit TFN Number"
      >
        <TFNNumberEdit
          handleTFNSumbit={handleTFNSumbit}
          refetchProfile={refetchProfile}
        />
      </Modal>

      <OTPModal
        visible={openTFNUpateOTPModal}
        onCancel={handleTFNUpdatCancel}
        onVerify={handleTFNOTPUpdateVerify}
        resend={false}
      />
    </>
  );
};

export default Profile;
